import React from "react";
import { fromJS, List } from "immutable";
import { Input, Select, SelectOption } from "@showitinc/showit-wc";

import AppConstants from "../../../../constants/AppConstants";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import {
  GRAPHIC_STYLE,
  GRAPHIC_STYLE_HORIZONTAL_SHIFT,
  GRAPHIC_STYLE_TYPE,
} from "../../../../constants/MergeConstants";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import { LAYOUT_MOBILE, LAYOUTS, SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import { convertToZoomMode, getDimensions } from "@/utils/CropUtils";
import { deleteProp } from "@/utils/PropUpdateUtils";
import LeftOffset from "../../../../components/base/LeftOffset";
import CustomizeEffects from "../../shared/CustomizeEffects";

const { GRAPHIC_SCALE_FILL, GRAPHIC_SCALE_CONTAIN, GRAPHIC_SCALE_STRETCH, GRAPHIC_SCALE_ZOOM } =
  AppConstants;

const defaults = fromJS({
  gs: {
    t: GRAPHIC_SCALE_FILL,
    p: "100",
    x: "50",
    y: "50",
    s: "50",
  },
});

const gatherableProps = [GRAPHIC_STYLE_TYPE];

const generateCropConversionUpdates = (element, selectedLayout) => {
  const isMerged = element.get(SYNCED_PROPS, List()).includes(GRAPHIC_STYLE_TYPE);
  const affectedLayouts = isMerged ? LAYOUTS : [selectedLayout];
  const aspectRatio = element.getIn(["content", "aspect_ratio"]);

  return affectedLayouts.reduce((result, layout) => {
    if (isMerged && layout === LAYOUT_MOBILE) {
      result.push(deleteProp(`${LAYOUT_MOBILE}.${GRAPHIC_STYLE}`));
    } else {
      const dimensions = getDimensions(element, layout);
      const convertedDimensions = convertToZoomMode(dimensions, aspectRatio);

      result.push({
        name: `${layout}.${GRAPHIC_STYLE}`,
        value: convertedDimensions.get("gs"),
      });
    }

    return result;
  }, []);
};

const handleFitUpdate = (element, layout, propUpdate, onPropUpdate) => {
  const updates = [propUpdate];

  if (propUpdate?.value === "zoom") {
    updates.push(...generateCropConversionUpdates(element, layout));
  }

  onPropUpdate(updates);
};

const ElementPropertiesGraphicScale = ({
  onPropUpdate,
  element,
  layout,
  isLocked,
  mappedElement,
}) => {
  const _onPropChange = (propInfo) => {
    onPropUpdate && onPropUpdate(propInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    _onPropChange,
    layout,
  );

  const elemProps = defaults.mergeDeep(gatherProps(element, layout, gatherableProps, true));
  const synced = isSynced(GRAPHIC_STYLE_TYPE);
  const isPlaceholder = element.getIn(["content", "key"]) === undefined;

  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];
  const getChildPropName = (propName) => getPropName(propName, synced);

  const { onChange: onSizeSelect } = initFormatter(
    (propUpdate) => handleFitUpdate(element, layout, propUpdate, _onPropChange),
    getPropName,
  );
  const { onChange } = initFormatter(onPropUpdate, getChildPropName);

  const objectFit = getValue(GRAPHIC_STYLE_TYPE);

  return (
    <>
      {!isPlaceholder && (
        <SplitMergeWrapper
          layout={layout}
          sync={synced}
          toggleSync={() => toggleSyncedProp(GRAPHIC_STYLE_TYPE)}
        >
          <Select
            context
            disabled={isLocked}
            label="Image Fit"
            value={objectFit}
            shift="left"
            onSISelect={onSizeSelect(...prepChange(GRAPHIC_STYLE_TYPE))}
          >
            <SelectOption value={GRAPHIC_SCALE_FILL}>Fill</SelectOption>
            <SelectOption value={GRAPHIC_SCALE_CONTAIN}>Contain</SelectOption>
            <SelectOption value={GRAPHIC_SCALE_STRETCH}>Stretch</SelectOption>
            <SelectOption value={GRAPHIC_SCALE_ZOOM}>Crop</SelectOption>
          </Select>
          {objectFit === GRAPHIC_SCALE_FILL && (
            <LeftOffset>
              <Input
                disabled={isLocked}
                label="Position"
                min="0"
                max="100"
                onSIBeforeChange={onChange(...prepChange(GRAPHIC_STYLE_HORIZONTAL_SHIFT))}
                onSIInput={onChange(...prepChange(GRAPHIC_STYLE_HORIZONTAL_SHIFT), false)}
                step="10"
                type={INPUT_TYPE_NUMBER}
                units="%"
                value={getValue(GRAPHIC_STYLE_HORIZONTAL_SHIFT)}
              />
            </LeftOffset>
          )}
        </SplitMergeWrapper>
      )}
      <CustomizeEffects
        mappedElement={mappedElement}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={element}
      />
    </>
  );
};

export default ElementPropertiesGraphicScale;
